import { HistoryProvider } from "@netlify/ui";
import { createMemoryHistory } from "history";
import React, { useMemo } from "react";
import { NetlifySDKProvider } from "../NetlifySDKProvider.jsx";
import { Theme } from "../theme/Theme.jsx";
import { useWindowResizer } from "../use_window_resizer.js";
import { FullstorySetup } from "./FullstorySetup.jsx";
import {
  LinkProvider,
  type LinkProviderRenderLink,
} from "./Link/LinkProvider.jsx";
import { defaultImportSvg, SvgProvider } from "./SvgProvider.jsx";
import { alertCN } from "./vendor/Alert/styles.js";

type ResizerProps = {
  children?: React.ReactNode | undefined;
};

const Resizer = ({ children }: ResizerProps) => {
  useWindowResizer();

  return <>{children}</>;
};

const renderLink: LinkProviderRenderLink = ({ href, ...otherProps }) => (
  <a {...otherProps} href={href} rel="noopener noreferrer" target="_blank" />
);

export type NetlifyExtensionUIProps = {
  children?: React.ReactNode | undefined;
};

/**
 * <!-- TODO(ndhoule): Document me -->
 *
 * @internal
 */
export const NetlifyExtensionUI = ({ children }: NetlifyExtensionUIProps) => {
  // XXX(ndhoule): This satisfies a dependency on history in @netlify/ui#Dropdown. We don't actually
  // expose the functionality that uses history in the SDK, so we use a memory history simply to
  // stub out the history dependency.
  const history = useMemo(createMemoryHistory, []);

  // TODO(ndhoule): Enable React.StrictMode
  // TODO(ndhoule): Add an error boundary
  // TODO(ndhoule): Optionally: Connect error boundary to Netlify-owned error system (e.g. Bugsnag)
  // https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
  if (window.parent === window) {
    return (
      <div
        className={alertCN({
          className: "tw-grow",
          outset: undefined,
          state: "warning",
        })}
      >
        Loading your extension outside of the Netlify app is unsupported.
        Navigate to the&nbsp;
        <a href="https://app.netlify.com">Netlify app</a>&nbsp;to develop your
        extension.
      </div>
    );
  }
  return (
    <NetlifySDKProvider>
      <HistoryProvider history={history}>
        <Resizer>
          <SvgProvider importSvg={defaultImportSvg}>
            <LinkProvider renderLink={renderLink}>
              <Theme />
              <FullstorySetup />
              {children}
            </LinkProvider>
          </SvgProvider>
        </Resizer>
      </HistoryProvider>
    </NetlifySDKProvider>
  );
};
